import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import { useDialogs } from "../../components/Dialogs";
import { RelativeTime } from "../../components/RelativeTime";
import { ErrorBox, Spinner } from "../../components/Spinner";

export function SecretSettings({ pid }: { pid: string }) {
  const qc = useQueryClient();
  const { confirm, prompt } = useDialogs();
  const { data, isLoading, error } = useQuery({ queryKey: qk.secrets(pid), queryFn: () => api.secrets(pid) });
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.secrets(pid) });

  const put = useMutation({
    mutationFn: (v: { name: string; value: string }) => api.putSecret(pid, v.name, v.value),
    onSuccess: () => {
      setName("");
      setValue("");
    },
    onSettled: invalidate,
  });
  const del = useMutation({ mutationFn: (n: string) => api.deleteSecret(pid, n), onSettled: invalidate });

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) put.mutate({ name: name.trim(), value });
  };

  return (
    <div>
      <p className="muted">
        Secrets are encrypted at rest and injected into environments as environment variables when a template lists
        them in <code>secret_env</code>. Values are never shown again after saving.
      </p>
      {isLoading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan={3} className="muted">
                  No secrets yet.
                </td>
              </tr>
            )}
            {data.map((s) => (
              <tr key={s.name}>
                <td>
                  <code className="code-inline">{s.name}</code>
                </td>
                <td className="muted">
                  <RelativeTime iso={s.updated_at} />
                </td>
                <td className="table-actions">
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      const v = await prompt({
                        title: `Replace ${s.name}`,
                        label: "New value",
                        inputType: "password",
                        confirmLabel: "Save",
                      });
                      if (v !== null) put.mutate({ name: s.name, value: v });
                    }}
                  >
                    Replace
                  </button>
                  <button
                    className="btn btn-sm btn-ghost-danger"
                    onClick={async () => {
                      if (await confirm({ title: `Delete ${s.name}?`, danger: true, confirmLabel: "Delete" }))
                        del.mutate(s.name);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="inline-form" onSubmit={add}>
        <input
          className="input input-mono"
          placeholder="NAME"
          value={name}
          onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))}
          aria-label="Secret name"
        />
        <input
          className="input"
          type="password"
          placeholder="Value"
          autoComplete="new-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Secret value"
        />
        <button className="btn btn-primary" disabled={!name.trim() || put.isPending}>
          Set secret
        </button>
      </form>
    </div>
  );
}
